import { useEffect, useState } from "react";

import {
  faArrowDown,
  faArrowUp,
  faClose,
  faExclamationTriangle,
} from "@fortawesome/pro-regular-svg-icons";
import { useReactFlow } from "@xyflow/react";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";

import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Input } from "src/base-components/Input";
import { Icon, IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { useAuthoringUIActions } from "src/flowContainer/AuthoringUIContext";
import { useCanvasSearchHighlight } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import {
  FieldMatch,
  FieldTypes,
} from "src/flowGraph/canvasSearch/extractNodeFields";
import { useCanvasSearch } from "src/flowGraph/canvasSearch/useCanvasSearch";
import { useTrackCanvasSearch } from "src/flowGraph/canvasSearch/useTrackCanvasSearch";
import { useCanvasSearchTermParam } from "src/flowGraph/useCanvasSearchTermParam";
import { OpenSearchWindowParams } from "src/flowGraph/useFlowGraphSearchWindow";
import {
  LeftPaneOptions,
  SchemaOptions,
  URLKeys,
} from "src/router/SearchParams";
import { useNodeHighlighting } from "src/store/NodeHighlighting";
import { useGraphStore } from "src/store/StoreProvider";

const assertWindowData = (data: unknown): data is OpenSearchWindowParams => {
  return Boolean(data && typeof data === "object");
};

const openPane = (
  match: FieldMatch | undefined,
  openParams: () => void,
  openInputSchema: () => void,
  openOutputSchema: () => void,
  openNode: (nodeId: string) => void,
): void => {
  if (!match) return;
  switch (match.fieldType) {
    case FieldTypes.PARAM:
      openParams();
      break;
    case FieldTypes.INPUT_SCHEMA:
      openInputSchema();
      break;
    case FieldTypes.OUTPUT_SCHEMA:
      openOutputSchema();
      break;
    default:
      openNode(match?.nodeId ?? "");
  }
};

export const FlowGraphSearchWindow: React.FC<FloatingWindowProps> = observer(
  ({ computePosition, id, data: windowData }) => {
    if (!assertWindowData(windowData)) {
      throw new Error("Invalid floating window data", windowData);
    }

    const { trackClose, trackQuery, trackNavigate } = useTrackCanvasSearch();

    const { setSearchTerm, clearSearchTerm } = useCanvasSearchTermParam();
    const { searchTerm, matches, isSearching, debouncedSearchTerm } =
      useCanvasSearch();
    const { setHighlightedMatch } = useCanvasSearchHighlight();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [_, setSearchParams] = useSearchParams();

    const { close: closeWindow } = useFloatingWindowsActions();
    const { position, windowRef } = useFloatingWindowPosition(computePosition);

    const { setSelectedNode } = useGraphStore();
    const { setNodeEditorOpen } = useAuthoringUIActions();
    const { highlightNodes, clearHighlighting } = useNodeHighlighting();
    const { getNode, fitView } = useReactFlow();

    const openInputSchemaPane = () => {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.set(URLKeys.LeftPane, LeftPaneOptions.Schema);
        prevSearchParams.set(URLKeys.Schema, SchemaOptions.Input);
        return prevSearchParams;
      });
    };

    const openParametersPane = () => {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.set(URLKeys.LeftPane, LeftPaneOptions.Parameters);
        return prevSearchParams;
      });
    };

    const openOutputSchemaPane = () => {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.set(URLKeys.LeftPane, LeftPaneOptions.Schema);
        prevSearchParams.set(URLKeys.Schema, SchemaOptions.Output);
        return prevSearchParams;
      });
    };

    const closeNodePane = () => {
      setSelectedNode(null);
      setNodeEditorOpen(false);
    };

    const closeLeftPane = () => {
      setSearchParams((prevSearchParams) => {
        const currentPane = prevSearchParams.get(URLKeys.LeftPane);
        if (
          currentPane === LeftPaneOptions.Schema ||
          currentPane === LeftPaneOptions.Parameters
        ) {
          prevSearchParams.delete(URLKeys.LeftPane);
          prevSearchParams.delete(URLKeys.Schema);
        }
        return prevSearchParams;
      });
    };

    const closePaneForMatch = (match: FieldMatch | undefined) => {
      if (!match) return;
      if (match.fieldType === FieldTypes.NODE) {
        if (!windowData.keepLeftPaneOpen) {
          closeLeftPane();
        }
      } else {
        closeNodePane();
      }
    };

    const focusNode = (nodeId: string) => {
      const node = getNode(nodeId);
      if (!node) return;

      fitView({
        padding: 0.2,
        includeHiddenNodes: false,
        maxZoom: 1,
        minZoom: 0.4,
        duration: 300,
        nodes: [node],
      });
    };

    const openNode = (nodeId: string) => {
      setSelectedNode(nodeId, false, true);

      // Wait for group expansion and graph repositioning before centering.
      // setSelectedNode with expandGroup=true may trigger toggleGroupNode
      // which calls positionGraph(), so we need enough time for the layout
      // to settle before focusing the viewport on the node.
      setTimeout(() => {
        focusNode(nodeId);
      }, 100);
    };

    useEffect(() => {
      setHighlightedMatch(matches[0] ?? null);
      highlightNodes(
        matches.filter((match) => match?.nodeId).map((match) => match.nodeId!),
      );
      closePaneForMatch(matches[0]);
      openPane(
        matches[0],
        openParametersPane,
        openInputSchemaPane,
        openOutputSchemaPane,
        openNode,
      );
      // We want to run this hook only when the debounced search term changes to avoid rendering loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);

    const handleSearchTermChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
      setCurrentIndex(0);
      setHighlightedMatch(null);
      setSearchTerm(e.target.value);
      trackQuery(e.target.value, matches.length);
    };

    const handleSearchKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        return;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        handleNavigate(e.key === "ArrowDown" ? "next" : "previous");
      }
    };

    const handleClose = () => {
      clearSearchTerm();
      setHighlightedMatch(null);
      clearHighlighting();
      closeWindow(id);
      trackClose(searchTerm, matches.length);
    };

    const handleNavigate = (direction: "previous" | "next") => {
      if (matches.length === 0) return;

      const delta = direction === "previous" ? -1 : 1;
      const newIndex = (currentIndex + delta + matches.length) % matches.length;

      setCurrentIndex(newIndex);

      const newHighlightedMatch = matches[newIndex];
      setHighlightedMatch(newHighlightedMatch);
      closePaneForMatch(newHighlightedMatch);
      openPane(
        newHighlightedMatch,
        openParametersPane,
        openInputSchemaPane,
        openOutputSchemaPane,
        openNode,
      );
      trackNavigate(direction, searchTerm, matches.length);
    };

    const hasResults = matches.length > 0;
    const resultsLabel = hasResults
      ? `${currentIndex + 1} of ${matches.length}`
      : "0 results";
    const fieldIdIsNotAvailable = matches[currentIndex]?.fieldId === null;

    return (
      <FloatingWindowInternal
        ref={windowRef}
        height="h-16"
        isPinned={false}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title=""
        width="w-90"
        draggable
        fullBodyWidth
        headerless
        onClose={handleClose}
      >
        <div className="flex h-full items-center gap-x-3 px-4" data-grabbable>
          <Input
            placeholder="Search for a field"
            type="text"
            value={searchTerm}
            autoFocus
            fullWidth
            onChange={handleSearchTermChange}
            onKeyDown={handleSearchKeyDown}
          />
          {searchTerm &&
            (isSearching ? (
              <div className="shrink-0">
                <Skeleton height="h-4" width="w-12" />
              </div>
            ) : (
              <div className="flex items-center gap-x-1 whitespace-nowrap">
                {hasResults && fieldIdIsNotAvailable && (
                  <Tooltip title="Exact match highlight is not available for this node">
                    <Icon
                      variant="warning"
                      icon={faExclamationTriangle}
                      size="xs"
                    />
                  </Tooltip>
                )}
                <span className="font-inter-normal-12px text-gray-500">
                  {resultsLabel}
                </span>
              </div>
            ))}
          {matches.length > 1 && (
            <>
              <IconButton
                dataLoc="flow-graph-search-up-arrow"
                icon={faArrowUp}
                size="xs"
                onClick={() => handleNavigate("previous")}
              />
              <IconButton
                dataLoc="flow-graph-search-down-arrow"
                icon={faArrowDown}
                size="xs"
                onClick={() => handleNavigate("next")}
              />
            </>
          )}
          <IconButton
            dataLoc="flow-graph-search-close-button"
            icon={faClose}
            size="xs"
            onClick={handleClose}
          />
        </div>
      </FloatingWindowInternal>
    );
  },
);
